"use strict";
/**
 * User Preferences Utilities
 * 統一處理主題和語言偏好的共用函數
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAuthLogout = exports.performLogoutCleanup = exports.clearAuthCookies = exports.clearLocalStorageExcept = exports.ensureCacheOwner = exports.syncUserPreferences = exports.syncLanguage = exports.updateLanguageOnServer = exports.initializeLanguage = exports.getInitialLanguage = exports.updateColorThemeOnServer = exports.applyColorTheme = exports.getInitialColorTheme = exports.updateThemeOnServer = exports.applyTheme = exports.getInitialTheme = void 0;
const cookies_1 = require("./cookies");
const i18nUtils_1 = require("./i18nUtils");
// ============================================
// Theme Utilities
// ============================================
/**
 * 從 localStorage 讀取初始主題，避免頁面閃爍
 */
const getInitialTheme = () => {
    if (typeof window === 'undefined')
        return 'dark';
    const saved = localStorage.getItem('userTheme');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
};
exports.getInitialTheme = getInitialTheme;
/**
 * 將主題應用到 document，並同步到 localStorage
 */
const applyTheme = (theme) => {
    if (typeof window === 'undefined')
        return;
    const currentTheme = document.documentElement.className;
    if (currentTheme !== theme) {
        document.documentElement.className = theme;
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('userTheme', theme);
    }
};
exports.applyTheme = applyTheme;
/**
 * 更新主題到後端 Identity Service
 */
const updateThemeOnServer = async (theme, identityServiceUrl) => {
    try {
        const token = (0, cookies_1.getCookie)('auth_token');
        const headers = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const response = await fetch(`${identityServiceUrl}/auth/theme`, {
            method: 'PATCH',
            headers,
            credentials: 'include',
            body: JSON.stringify({ theme }),
        });
        return response.ok;
    }
    catch (error) {
        console.warn('Failed to update theme on server:', error);
        return false;
    }
};
exports.updateThemeOnServer = updateThemeOnServer;
const VALID_COLOR_THEMES = ['blue', 'violet', 'emerald', 'teal', 'amber', 'zinc'];
const COLOR_THEME_STORAGE_KEY = 'account-portal-color-theme';
/**
 * 從 localStorage 讀取初始配色主題
 */
const getInitialColorTheme = () => {
    if (typeof window === 'undefined')
        return 'blue';
    const saved = localStorage.getItem(COLOR_THEME_STORAGE_KEY);
    return VALID_COLOR_THEMES.includes(saved) ? saved : 'blue';
};
exports.getInitialColorTheme = getInitialColorTheme;
/**
 * 將配色主題應用到 document，並同步到 localStorage
 */
const applyColorTheme = (colorTheme) => {
    if (typeof window === 'undefined')
        return;
    document.documentElement.setAttribute('data-color-theme', colorTheme);
    localStorage.setItem(COLOR_THEME_STORAGE_KEY, colorTheme);
};
exports.applyColorTheme = applyColorTheme;
/**
 * 更新配色主題到後端 Identity Service
 */
const updateColorThemeOnServer = async (colorTheme, identityServiceUrl) => {
    try {
        const token = (0, cookies_1.getCookie)('auth_token');
        const headers = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const response = await fetch(`${identityServiceUrl}/auth/color-theme`, {
            method: 'PATCH',
            headers,
            credentials: 'include',
            body: JSON.stringify({ colorTheme }),
        });
        return response.ok;
    }
    catch (error) {
        console.warn('Failed to update color theme on server:', error);
        return false;
    }
};
exports.updateColorThemeOnServer = updateColorThemeOnServer;
// ============================================
// Language Utilities
// ============================================
const SUPPORTED_LANGUAGES = ['zh', 'en', 'ja'];
/**
 * 初始語言：localStorage 選過的 → 瀏覽器語言（中文 zh、日文 ja、其他 en）
 * 登入後會再以帳號設定為準（syncUserPreferences）
 */
const getInitialLanguage = () => (0, i18nUtils_1.getPreferredLanguage)(SUPPORTED_LANGUAGES);
exports.getInitialLanguage = getInitialLanguage;
/**
 * 初始化語言並同步到 i18n
 * @param changeLanguage - i18n.changeLanguage 函數
 */
const initializeLanguage = (changeLanguage) => {
    const language = (0, exports.getInitialLanguage)();
    changeLanguage(language);
    return language;
};
exports.initializeLanguage = initializeLanguage;
/**
 * 更新語言到後端 Identity Service
 */
const updateLanguageOnServer = async (language, identityServiceUrl) => {
    try {
        const token = (0, cookies_1.getCookie)('auth_token');
        const headers = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const response = await fetch(`${identityServiceUrl}/auth/language`, {
            method: 'PATCH',
            headers,
            credentials: 'include',
            body: JSON.stringify({ language }),
        });
        return response.ok;
    }
    catch (error) {
        console.warn('Failed to update language on server:', error);
        return false;
    }
};
exports.updateLanguageOnServer = updateLanguageOnServer;
/**
 * 同步語言設定（更新 state、i18n、localStorage）
 */
const syncLanguage = (language, changeLanguage) => {
    changeLanguage(language);
    localStorage.setItem('userLanguage', language);
};
exports.syncLanguage = syncLanguage;
/**
 * 從用戶資料同步主題和語言偏好
 * 用於 initializeAuth 和 refreshUser
 */
const syncUserPreferences = (userPreferences, handlers) => {
    const { theme, language, colorTheme } = userPreferences;
    const { setTheme, setLanguage, changeLanguage, setColorTheme, currentTheme, currentLanguage, currentColorTheme } = handlers;
    // 同步深淺主題
    if (theme && theme !== currentTheme) {
        setTheme(theme);
    }
    // 同步語言
    if (language) {
        if (language !== currentLanguage) {
            setLanguage(language);
        }
        (0, exports.syncLanguage)(language, changeLanguage);
    }
    // 同步配色主題
    if (colorTheme && VALID_COLOR_THEMES.includes(colorTheme) && colorTheme !== currentColorTheme) {
        if (setColorTheme) {
            setColorTheme(colorTheme); // 內部已呼叫 applyColorTheme
        }
        else {
            (0, exports.applyColorTheme)(colorTheme); // fallback：無 context 時直接套用
        }
    }
};
exports.syncUserPreferences = syncUserPreferences;
// ============================================
// Logout Utilities
// ============================================
const DEFAULT_KEYS_TO_KEEP = ['userTheme', 'userLanguage', 'account-portal-color-theme'];
const CACHE_OWNER_KEY = 'cache-owner';
/**
 * 換帳號時清掉上一個帳號的快取（列表、儀表板等 persist 在 localStorage 的資料不分使用者）
 * 登入驗證成功後呼叫：快取不是這個帳號的就清掉並回傳 true，呼叫端重新載入一次頁面，
 * 已經從 localStorage 還原到記憶體的 store 才會一起清掉。
 * 第一次（還沒有記錄）只記下目前帳號，不清除，避免更新上線時所有人都重新載入一次。
 * 例：在 portal 登出、改登另一個帳號，再開 PM 時原本會先畫出前一個帳號的專案與金額。
 */
const ensureCacheOwner = (owner) => {
    try {
        const current = `${owner.id}:${owner.organizationId ?? ''}`;
        const previous = localStorage.getItem(CACHE_OWNER_KEY);
        if (previous === current)
            return false;
        const changed = previous !== null;
        if (changed) {
            (0, exports.clearLocalStorageExcept)([...DEFAULT_KEYS_TO_KEEP, 'i18nextLng']); // 不保留舊版的 localStorage token：換帳號後會以前一個帳號身分呼叫 API
            sessionStorage.clear();
        }
        localStorage.setItem(CACHE_OWNER_KEY, current);
        return changed;
    }
    catch {
        return false; // localStorage 不可用時略過
    }
};
exports.ensureCacheOwner = ensureCacheOwner;
/**
 * 清理 localStorage，保留指定的 keys
 */
const clearLocalStorageExcept = (keysToKeep = DEFAULT_KEYS_TO_KEEP) => {
    try {
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
            if (!keysToKeep.includes(key)) {
                localStorage.removeItem(key);
            }
        });
    }
    catch (error) {
        console.warn('Failed to clear localStorage:', error);
    }
};
exports.clearLocalStorageExcept = clearLocalStorageExcept;
/**
 * 清理所有認證相關的 cookies
 */
const clearAuthCookies = () => {
    try {
        const cookiesToClear = ['auth_token'];
        cookiesToClear.forEach(cookieName => {
            // 清理根路徑
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            // 清理當前域名
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            // 清理可能的子域 cookie
            const hostname = window.location.hostname;
            const parts = hostname.split('.');
            if (parts.length >= 2) {
                const domain = `.${parts.slice(-2).join('.')}`;
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
            }
        });
    }
    catch (error) {
        console.warn('Failed to clear auth cookies:', error);
    }
};
exports.clearAuthCookies = clearAuthCookies;
/**
 * 執行完整的登出清理流程
 */
const performLogoutCleanup = (clearStoresCallback) => {
    try {
        // 1. 清理 stores（如果有提供）
        if (clearStoresCallback) {
            clearStoresCallback();
        }
        // 2. 清理 localStorage
        (0, exports.clearLocalStorageExcept)();
        // 3. 清理 sessionStorage
        sessionStorage.clear();
        // 4. 清理 cookies
        (0, exports.clearAuthCookies)();
        console.log('Cache and temporary data cleared successfully');
    }
    catch (error) {
        console.warn('Failed to clear some cache data:', error);
    }
};
exports.performLogoutCleanup = performLogoutCleanup;
/**
 * 建立登出重定向函數
 */
const createAuthLogout = (identityServiceUrl, includeRedirect = true) => {
    return () => {
        if (includeRedirect) {
            const currentOrigin = window.location.origin;
            const redirectUrl = encodeURIComponent(currentOrigin);
            window.location.href = `${identityServiceUrl}/auth/logout?redirectUrl=${redirectUrl}`;
        }
        else {
            window.location.href = `${identityServiceUrl}/auth/logout`;
        }
    };
};
exports.createAuthLogout = createAuthLogout;
